import MapLibreGL from "maplibre-gl";

enum DotNetMethod {
    HandleClickAsync = "HandleClickAsync",
    HandleMouseEnterAsync = "HandleMouseEnterAsync",
    HandleMouseLeaveAsync = "HandleMouseLeaveAsync"
}

type MapRouteOptions = {
    sourceId: string;
    layerId: string;
    color: string;
    width: number;
    opacity: number;
    dashArray?: number[];
    interactive: boolean;
}

type Entry = {
    map: MapLibreGL.Map;
    sourceId: string;
    layerId: string;
    interactive: boolean;
    handleClick?: () => void;
    handleMouseEnter?: () => void;
    handleMouseLeave?: () => void;
};

const registry = new Map<any, Entry>();

function initialize(dotNetHelper: any, map: MapLibreGL.Map, options: MapRouteOptions): void {
    const {
        sourceId,
        layerId,
        color,
        width,
        opacity,
        dashArray,
        interactive
    } = options;

    map.addSource(sourceId, {
        type: "geojson",
        data: {
            type: "Feature",
            properties: {},
            geometry: { type: "LineString", coordinates: [] },
        },
    });

    map.addLayer({
        id: layerId,
        type: "line",
        source: sourceId,
        layout: { "line-join": "round", "line-cap": "round" },
        paint: {
            "line-color": color,
            "line-width": width,
            "line-opacity": opacity,
            ...(dashArray && { "line-dasharray": dashArray })
        },
    });

    const entry: Entry = { map, sourceId, layerId, interactive };

    if (interactive) {

        const handleClick = () => {
            dotNetHelper.invokeMethodAsync(DotNetMethod.HandleClickAsync);
        };

        const handleMouseEnter = () => {
            map.getCanvas().style.cursor = "pointer";
            dotNetHelper.invokeMethodAsync(DotNetMethod.HandleMouseEnterAsync);
        };

        const handleMouseLeave = () => {
            map.getCanvas().style.cursor = "";
            void dotNetHelper.invokeMethodAsync(DotNetMethod.HandleMouseLeaveAsync);
        };

        map.on("click", layerId, handleClick);
        map.on("mouseenter", layerId, handleMouseEnter);
        map.on("mouseleave", layerId, handleMouseLeave);

        entry.handleClick = handleClick;
        entry.handleMouseEnter = handleMouseEnter;
        entry.handleMouseLeave = handleMouseLeave;
    }

    registry.set(dotNetHelper, entry);
}

function updateData(map: MapLibreGL.Map, sourceId: string, coordinates: [number, number][]): void {
    const source = map.getSource(sourceId) as MapLibreGL.GeoJSONSource;
    if (!source) return;

    source.setData({
        type: "Feature",
        properties: {},
        geometry: { type: "LineString", coordinates },
    });
}

function updateStyle(
    map: MapLibreGL.Map,
    layerId: string,
    options: {
        color: string;
        width: number;
        opacity: number;
        dashArray: number[];
    }
): void {
    const { color, width, opacity, dashArray } = options;

    if (!map.getLayer(layerId)) return;

    map.setPaintProperty(layerId, "line-color", color);
    map.setPaintProperty(layerId, "line-width", width);
    map.setPaintProperty(layerId, "line-opacity", opacity);
    if (dashArray) {
        map.setPaintProperty(layerId, "line-dasharray", dashArray);
    }
}

function destroy(dotNetHelper: any): void {
    const entry = registry.get(dotNetHelper);
    if (!entry) return;

    const {
        map,
        layerId,
        sourceId,
        interactive,
        handleClick,
        handleMouseEnter,
        handleMouseLeave
    } = entry;

    if (interactive) {
        if (handleClick) map.off("click", layerId, handleClick);
        if (handleMouseEnter) map.off("mouseenter", layerId, handleMouseEnter);
        if (handleMouseLeave) map.off("mouseleave", layerId, handleMouseLeave);
    }

    if (map.getLayer(layerId)) map.removeLayer(layerId);
    if (map.getSource(sourceId)) map.removeSource(sourceId);

    dotNetHelper.dispose();

    registry.delete(dotNetHelper);
}

export default {
    initialize,
    updateData,
    updateStyle,
    destroy
}